interface HttpErrorLike {
  status: number;
  statusText?: string;
  data?: unknown;
  message?: string;
}

const RELOAD_COUNT_KEY = 'errorBoundaryReloadCount';
const RELOAD_TIMESTAMP_KEY = 'errorBoundaryReloadTimestamp';
const MAX_RELOAD_ATTEMPTS = 3; // heuristic value
const RELOAD_COUNT_RESET_MS = 1 * 60 * 1000; // heuristic value

function isHttpError(error: unknown): error is HttpErrorLike {
  return (
    typeof error === 'object' &&
    error !== null &&
    'status' in error &&
    typeof (error as Record<string, unknown>).status === 'number'
  );
}

function extractErrorInfo(error: unknown): Record<string, unknown> {
  if (isHttpError(error)) {
    return {
      type: 'HttpError',
      status: error.status,
      statusText: error.statusText,
      message: error.message,
      stack: error instanceof Error ? error.stack : undefined,
      data: typeof error.data === 'object' ? JSON.stringify(error.data) : error.data,
    };
  }

  if (error instanceof Error) {
    return {
      type: 'Error',
      name: error.name,
      message: error.message,
      stack: error.stack,
    };
  }

  if (typeof error === 'object' && error !== null) {
    try {
      return { type: 'Unknown', raw: JSON.stringify(error) };
    } catch {
      return { type: 'Unknown', raw: String(error) };
    }
  }

  return { type: 'Unknown', raw: String(error) };
}

function isVersionMismatchError(error: unknown): boolean {
  if (!(error instanceof Error)) {
    return false;
  }

  if (error.name === 'ChunkLoadError') {
    return true;
  }

  const message = error.message || '';
  const stack = error.stack || '';

  if (error.name === 'SyntaxError') {
    const syntaxErrorPatterns = [/Unexpected token/i, /Unexpected identifier/i, /expected expression/i];

    if (syntaxErrorPatterns.some((pattern) => pattern.test(message))) {
      return true;
    }
  }

  // finally check if the error is related to chunk loading
  if (/Loading chunk .* failed/i.test(message) || /Loading chunk .* failed/i.test(stack)) {
    return true;
  }

  return false;
}

function shouldAttemptReload(storage: Storage = sessionStorage, now: () => number = Date.now): boolean {
  try {
    const countStr = storage.getItem(RELOAD_COUNT_KEY);
    const timestampStr = storage.getItem(RELOAD_TIMESTAMP_KEY);

    let reloadCount = countStr ? parseInt(countStr, 10) : 0;
    const lastTimestamp = timestampStr ? parseInt(timestampStr, 10) : 0;

    if (lastTimestamp && now() - lastTimestamp > RELOAD_COUNT_RESET_MS) {
      reloadCount = 0;
    }

    if (reloadCount >= MAX_RELOAD_ATTEMPTS) {
      return false;
    }

    storage.setItem(RELOAD_COUNT_KEY, (reloadCount + 1).toString());
    storage.setItem(RELOAD_TIMESTAMP_KEY, now().toString());

    return true;
  } catch {
    return true;
  }
}

function clearReloadCount(storage: Storage = sessionStorage): void {
  try {
    storage.removeItem(RELOAD_COUNT_KEY);
    storage.removeItem(RELOAD_TIMESTAMP_KEY);
  } catch {
    // ignore
  }
}

export {
  isHttpError,
  extractErrorInfo,
  isVersionMismatchError,
  shouldAttemptReload,
  clearReloadCount,
  RELOAD_COUNT_KEY,
  RELOAD_TIMESTAMP_KEY,
  MAX_RELOAD_ATTEMPTS,
  RELOAD_COUNT_RESET_MS,
};
export type { HttpErrorLike };
